import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import FragranceProvider from './contexts/FragranceContext';
import { WishlistProvider } from './contexts/WishlistContext';
import LandingPage from './components/auth/LandingPage';
import DashboardLayout from './components/dashboard/DashboardLayout';
import PublicCollectionView from './components/public/PublicCollectionView';
import EmailVerification from './components/auth/EmailVerification';
import EmailChangeConfirmation from './components/auth/EmailChangeConfirmation';
import PasswordReset from './components/auth/PasswordReset';
import AccountDeletionConfirmation from './components/auth/AccountDeletionConfirmation';
import LoadingSpinner from './components/ui/LoadingSpinner';

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, resendVerificationEmail, logout, user, emailVerificationRequired } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check if email verification is required
  if (emailVerificationRequired || (user && !user.email_verified)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="text-6xl mb-4">📧</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Email Verification Required
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please check your email and click the verification link to complete your registration.
          </p>
          <button
            onClick={async () => {
              const result = await resendVerificationEmail();
              if (result.success) {
                alert('Verification email sent! Please check your inbox.');
              } else {
                alert(result.message);
              }
            }}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 mb-4"
          >
            Resend Verification Email
          </button>
          <button
            onClick={logout}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Main app component
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  return (
    <Router>
      <Routes>
        <Route 
          path="/" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <FragranceProvider>
                <WishlistProvider>
                  <DashboardLayout />
                </WishlistProvider>
              </FragranceProvider>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/collection/:username" 
          element={<PublicCollectionView />} 
        />
        <Route 
          path="/verify-email" 
          element={<EmailVerification />} 
        />
        <Route 
          path="/confirm-email-change" 
          element={<EmailChangeConfirmation />} 
        />
        <Route 
          path="/reset-password" 
          element={<PasswordReset />} 
        />
        <Route 
          path="/confirm-account-deletion" 
          element={<AccountDeletionConfirmation />} 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
